
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { ROUTER } from '@/router/constants';
import {
	Runnit,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RUNNIT_TYPE,
	RUNNITS_ACCESS_LEVEL,
} from '@run-diffusion/shared';
import RunnitPageSettings from '@/views/Runnits/RunnitPageSettings.vue';
import ImageGalleryDrawer from '@/components/ImageGallery/ImageGalleryDrawer.vue';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import pluralize from 'pluralize';
import _isEmpty from 'lodash/isEmpty';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import GreyButton from '@/components/base/GreyButton.vue';
import { RUNNITS_OWNER_SELECTION } from './constants';
import RunnitTitleBar from '@/views/Runnits/RunnitTitleBar.vue';
import { TeamUserMixin } from '@/mixins';
import * as amplitude from '@amplitude/analytics-browser';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { RunnitsCRUDMixin } from '@/mixins/RunnitsCRUDMixin';
import { RunnitsOwnerSelection } from '@/store';
import { featureTourService } from '@/featureTour/services/featureTourService';
import { isFeatureTourEligibleViewport } from '@/featureTour/utils/viewport';
import { boardFirstOpen, singleToolFirstOpen } from '@/featureTour/tours';
import { SELECTED_PROMPT_VIEW } from '@/views/Runnits/constants';

export default Vue.extend({
	name: 'RunnitsApp',
	metaInfo: {
		titleTemplate: '%s | Runnit | RunDiffusion',
	},
	mixins: [
		RunnitsImageSelectMixin,
		RunnitBulkActionsMixin,
		TeamUserMixin,
		RunnitsCRUDMixin,
	],
	data () {
		return {
			RUNNIT_TYPE,

			deleteImagesDialog: {
				open: false,
				deleting: false,
			},

			selectedNodeDef: null,
		};
	},
	created () {
		// Set initial history length when entering the app
		this.updateRunnitState({
			initialHistoryLength: window.history.length
		});

		// Force collapse side nav on mount for mdAndUp screens
		if (this.$vuetify.breakpoint.mdAndUp) {
			this.updateToolbar({
				runnitsSideNavCollapsed: true,
				// Do not change userManuallyCollapsedSideNav here
			});
		}
	},
	beforeDestroy () {
		// Expand side nav on destroy if user hasn't manually collapsed it (and not on mobile)
		if (this.$vuetify.breakpoint.mdAndUp && !this.toolbar.userManuallyCollapsedSideNav) {
			this.updateToolbar({
				runnitsSideNavCollapsed: false,
			});
		}
	},
	watch: {
		'runnitState.runnitsOwnerSelection': {
			immediate: true,
			async handler (newVal: RunnitsOwnerSelection, oldVal: RunnitsOwnerSelection) {
				if (oldVal && newVal !== oldVal) {
					// Don't redirect if viewing a featured template (accessible regardless of owner selection)
					const currentRunnit = this.runnitState?.runnit;
					if (currentRunnit?.type === RUNNIT_TYPE.TEMPLATE && currentRunnit?.isFeatured) {
						return;
					}
					this.routeAwayDueToRunnitOwnershipIssue();
				}
			}
		},
		limitNumRunnitsMap: {
			immediate: true,
			async handler (newVal: Record<string, Runnit>, oldVal: Record<string, Runnit>) {
				const currentRunnit = this.runnitState?.runnit;
				// Don't check limits for templates (they're viewable by anyone)
				if (currentRunnit?.type === RUNNIT_TYPE.TEMPLATE) {
					return;
				}
				if (newVal !== oldVal && this.isAbovePlanRunnitsLimit(currentRunnit, newVal)) {
					this.routeAwayDueToRunnitsLimit();
				}
			},
		},
		'runnitState.runnit': {
			immediate: true,
			async handler (newVal: Runnit, oldVal: Runnit) {
				// Don't check limits for templates (they're viewable by anyone)
				if (newVal?.type === RUNNIT_TYPE.TEMPLATE) {
					// Skip limit check, but continue with ownership check below
				} else if (newVal !== oldVal && this.isAbovePlanRunnitsLimit(newVal, this.limitNumRunnitsMap)) {
					this.routeAwayDueToRunnitsLimit();
				}

				// Runnit doesn't belong to the current Team
				const ownerSelection = this.runnitState.runnitsOwnerSelection;
				if (
					newVal &&
					oldVal &&
					(
						ownerSelection &&
						(
							ownerSelection === RUNNITS_OWNER_SELECTION.USER &&
							newVal.teamId !== null
						) ||
						(
							newVal.type === RUNNIT_TYPE.WORKFLOW &&
							ownerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
							this.team &&
							this.team.id !== newVal.teamId
						)
					) &&
					// Featured templates should be accessible regardless of team ownership
					!(newVal.type === RUNNIT_TYPE.TEMPLATE && newVal.isFeatured)
				) {
					this.routeAwayDueToRunnitOwnershipIssue();
				}
			}
		},
		// Watch for team changes to detect when user switches teams
		team: {
			immediate: false,
			async handler (newVal: any, oldVal: any) {
				// If team changes and we have a workflow runnit loaded that doesn't belong to the new team
				if (
					newVal &&
					oldVal &&
					newVal.id !== oldVal.id &&
					this.runnitState?.runnit &&
					this.runnitState.runnit.type === RUNNIT_TYPE.WORKFLOW &&
					this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
					this.runnitState.runnit.teamId !== newVal.id
				) {
					this.routeAwayDueToRunnitOwnershipIssue();
				}
			}
		},
		bulkDeleteConfirmDialogOpen: {
			immediate: true,
			async handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal && !newVal && this.$refs.doubleConfirmDangerDialog) {
					this.$refs.doubleConfirmDangerDialog.reset();
				}
			}
		},
		'runnitState.selectedNodeId': {
			immediate: true,
			handler (newVal: string) {
				if (newVal) {
					this.waitForNodeDef(newVal);
				} else {
					this.selectedNodeDef = null;
				}
			},
		},
	},
	computed: {
		...mapState([
			'user',
			'runnitState',
			'limitNumRunnitsMap',
			'isAdminProxyingUser',
			'team',
			'runnitNodesMap',
			'runnitNodes',
			'toolbar',
		]),
		currentPageType () {
			// Determine the current page type based on the runnit type and nodes
			if (!this.runnitState?.runnit) return null;

			const runnitType = this.runnitState.runnit.type;
			const hasTrainingNode = this.runnitNodes?.some(
				node => node?.nodeDef?.appType === 'TRAINER'
			);

			if (hasTrainingNode) return 'training';
			if (runnitType === 'SINGLE_TOOL') return 'single-tool';
			if (runnitType === 'TEMPLATE') return 'template';
			return 'workflow';
		},
		isTrainerAppType () {
			const selectedNodeId = this.runnitState.selectedNodeId ? this.runnitState.selectedNodeId : this.runnitNodes[0]?.id;
			return this.runnitNodesMap &&
				selectedNodeId &&
				this.runnitNodesMap[selectedNodeId]?.nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER;
		},
		hasHistory () {
			// Check if we're not on the root runnits route
			return !this.$route.meta.isSessionPage &&
				(this.$route.name !== ROUTER.RUNNITS_HOME ||
					window.history.length > this.runnitState.initialHistoryLength);
		},
		showFeatureTourLauncher (): boolean {
			// Workflow boards AND templates both get the launcher (boardFirstOpen
			// adapts its content — step 6 swaps Run for Create Board on
			// templates, etc.). Single-tool boards get a separate tour
			// (singleToolFirstOpen) but only when the prompt-driven form is the
			// active layout — the tour walks PrimaryForm anchors that don't
			// exist in drawer mode. Trainer pages are excluded.
			if (!isFeatureTourEligibleViewport(this.$vuetify)) return false;
			if (this.isTrainerAppType) return false;
			if (!this.runnitState?.runnit) return false;

			if (this.runnitState.runnit.type === RUNNIT_TYPE.SINGLE_TOOL) {
				const selectedNodeId = this.runnitState?.selectedNodeId;
				const selectedNode = selectedNodeId ? this.runnitNodesMap?.[selectedNodeId] : null;
				const nodeDef = selectedNode?.nodeDef;
				const supportsPromptDriven = !!(nodeDef && typeof nodeDef !== 'string' && nodeDef.usePromptDrivenForm);
				const userPrefersPromptDriven = this.runnitState?.selectedPromptView === SELECTED_PROMPT_VIEW.PROMPT_DRIVEN;
				return supportsPromptDriven && userPrefersPromptDriven;
			}

			return true;
		},
		featureTourLauncherTooltip (): string {
			if (this.currentPageType === 'single-tool') {
				return 'Take a quick tour of Single Tool mode — prompt, settings, running the tool, and finding your generations.';
			}
			return 'Take a quick tour of how boards work — prompts, settings, running tools, and chaining steps into a workflow.';
		},
	},
	methods: {
		...mapActions([
			'updateToolbar',
			'updateRunnitState',
			'updateSnackbar',
		]),
		pluralize,
		onBackClick () {
			if (!this.$route.meta.isSessionPage) {
				this.$router.back();
			}
		},
		onGalleryDrawerClose (open: boolean) {
			this.updateRunnitState({
				imageGalleryDrawerOpen: open,
			});
		},
		isAbovePlanRunnitsLimit (runnit: Runnit, limitNumRunnitsMap: Record<string, Runnit>) {
			// Has the user hit their plan limit on number of Runnits
			return !!(
				runnit &&
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER && // Only individual plans have Board limits
				!this.user.clubInfo?.limit?.unlimitedRunnits && // Check if not unlimited runnits
				this.$route.params.runnitId === runnit.id && // We're on a page for this runnit right now
				runnit.type !== RUNNIT_TYPE.SINGLE_TOOL && // Single tool runnits are available to everyone
				runnit.type !== RUNNIT_TYPE.TEMPLATE && // Templates are available to everyone (limit is for creating boards, not viewing templates)
				!_isEmpty(limitNumRunnitsMap) && // It's loaded, and has at least one in there
				!limitNumRunnitsMap[runnit.id]
			);
		},
		routeAwayDueToRunnitsLimit () {
			console.log('routeAwayDueToRunnitsLimit')
			this.routerReplace(this.$route, this.$router, {
				name: ROUTER.RUNNITS_HOME,
			});
		},
		routeAwayDueToRunnitOwnershipIssue () {
			// If user is on a specific board, route to boards list since the board doesn't belong to the new team
			const routeName = this.$route.name === ROUTER.RUNNITS_BOARD
				? ROUTER.RUNNIT_BOARDS_PAGE
				: ROUTER.RUNNITS_HOME;

			this.routerReplace(this.$route, this.$router, {
				name: routeName,
			});
		},
		openSideNav () {
			this.updateToolbar({
				runnitsSideNavOpen: true,
			});
		},
		waitForNodeDef (nodeId: string, retries: number = 0) {
			if (retries > 10) {
				return;
			}

			const nodeDef = nodeId && this.runnitNodesMap && this.runnitNodesMap[nodeId]?.nodeDef;

			if (!nodeDef) {
				setTimeout(() => {
					this.waitForNodeDef(nodeId, retries + 1);
				}, 100);
				return;
			}

			if (typeof nodeDef === 'string' || !nodeDef.id) {
				setTimeout(() => {
					this.waitForNodeDef(nodeId, retries + 1);
				}, 100);
				return;
			}
			this.selectedNodeDef = nodeDef;
		},
		openCreateTemplateDialog () {
			amplitude.track(`Open Create Template Dialog`, { location: 'RunnitsApp Top Bar' });
			this.updateRunnitState({
				createRunnitTemplateOpen: true,
			});
		},
		launchFeatureTour () {
			const isSingleTool = this.currentPageType === 'single-tool';
			amplitude.track(
				isSingleTool ? `Launch Single Tool Feature Tour` : `Launch Board Feature Tour`,
				{ location: 'RunnitsApp Top Bar' },
			);
			featureTourService.start(isSingleTool ? singleToolFirstOpen : boardFirstOpen);
		},
		async createBoardFromTemplate (): Promise<void> {
			try {
				// Check if user can create a new runnit (this will show upsell dialog if limit reached)
				if (!this.canCreateNewRunnit(false)) {
					return;
				}

				amplitude.track(`Open Create Board from Template`, { location: 'RunnitsApp Top Bar' });
				// Create a draft runnit based on the template
				const clonedRunnit = {
					...this.runnitState.runnit,
					userId: this.user.id, // Set the current user as owner
					title: `${this.runnitState.runnit.title} (Copy)`, // Add (Copy) to distinguish from template
					createdAt: new Date(),
					deletedAt: null,
					// Reset template-specific fields
					type: RUNNIT_TYPE.WORKFLOW,
					accessLevel: RUNNITS_ACCESS_LEVEL.PRIVATE,
					teamId: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? this.team?.id : null,
				};
				delete clonedRunnit.id;

				// Update the runnit state to open the edit dialog with the cloned runnit
				this.updateRunnitState({
					runnitDraft: clonedRunnit,
					runnitDraftNodeRef: null,
					configureRunnitOpen: true,
					// Set a flag to indicate this is a template clone
					cloneFromTemplate: true,
					templateRunnit: this.runnitState.runnit,
				});
			} catch (error) {
				console.error('Error creating board from template:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error creating board from template, please try again',
					show: true,
				});
			}
		},
	},
	components: {
		RunnitTitleBar,
		GreyButton,
		ImageGalleryDrawer,
		RunnitPageSettings,
		DoubleConfirmDangerDialog,
	},
});
